import React, { useMemo } from 'react';
import { Box, Chip, Paper, Typography, useTheme } from '@mui/material';
import { ShowChart, Warning } from '@mui/icons-material';
import {
  ComposedChart,
  Area,
  Line,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceArea,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import {
  UnitPreferences,
  metersToFeet,
  celsiusToFahrenheit,
} from '../utils/unitConversions';

interface DepthReading {
  timestamp: string;
  depth: number; // meters
}

interface TemperatureReading {
  timestamp: string;
  temperature: number; // celsius
}

interface DiveProfileChartProps {
  depthReadings: DepthReading[];
  temperatureReadings?: TemperatureReading[];
  unitPreferences: UnitPreferences;
}

interface ProfilePoint {
  minutes: number;
  depth: number;
  temperature?: number;
  violation?: number; // depth value repeated when ascent rate exceeded, for scatter overlay
}

// NAUI/PADI guidance: keep ascents at or under ~9-10 m/min (30 ft/min)
const MAX_ASCENT_RATE_M_PER_MIN = 10;
// Rate is measured over a trailing window to avoid flagging single-sample sensor noise
const ASCENT_WINDOW_SECONDS = 10;
const SAFETY_STOP_TOP_M = 3;
const SAFETY_STOP_BOTTOM_M = 6;
const MAX_CHART_POINTS = 600;

const buildProfile = (
  depthReadings: DepthReading[],
  temperatureReadings: TemperatureReading[],
): { points: Array<ProfilePoint & { seconds: number }>; maxAscentRate: number } => {
  const parsed = depthReadings
    .map(r => ({ time: new Date(r.timestamp).getTime(), depth: r.depth }))
    .filter(r => Number.isFinite(r.time) && Number.isFinite(r.depth) && r.depth >= 0)
    .sort((a, b) => a.time - b.time);

  if (parsed.length < 2) return { points: [], maxAscentRate: 0 };

  const start = parsed[0].time;

  const temps = temperatureReadings
    .map(r => ({ time: new Date(r.timestamp).getTime(), temperature: r.temperature }))
    .filter(r => Number.isFinite(r.time) && Number.isFinite(r.temperature))
    .sort((a, b) => a.time - b.time);

  let tempIdx = 0;
  let maxAscentRate = 0;

  const points = parsed.map((r, i) => {
    const seconds = (r.time - start) / 1000;

    // Latest temperature reading at or before this sample
    while (tempIdx + 1 < temps.length && temps[tempIdx + 1].time <= r.time) tempIdx++;
    const temperature =
      temps.length > 0 && temps[tempIdx].time <= r.time ? temps[tempIdx].temperature : undefined;

    // Ascent rate over a trailing window
    let violation: number | undefined;
    let j = i - 1;
    while (j > 0 && (r.time - parsed[j].time) / 1000 < ASCENT_WINDOW_SECONDS) j--;
    if (j >= 0 && j < i) {
      const dtMinutes = (r.time - parsed[j].time) / 60000;
      if (dtMinutes > 0) {
        const rate = (parsed[j].depth - r.depth) / dtMinutes; // positive = ascending
        if (rate > maxAscentRate) maxAscentRate = rate;
        if (rate > MAX_ASCENT_RATE_M_PER_MIN) violation = r.depth;
      }
    }

    return { minutes: seconds / 60, seconds, depth: r.depth, temperature, violation };
  });

  return { points, maxAscentRate };
};

// Max ascent rate in m/min for a set of depth readings (0 if not computable)
export const computeMaxAscentRate = (depthReadings: DepthReading[]): number =>
  buildProfile(depthReadings, []).maxAscentRate;

const downsample = (
  points: Array<ProfilePoint & { seconds: number }>,
): Array<ProfilePoint & { seconds: number }> => {
  if (points.length <= MAX_CHART_POINTS) return points;
  const bucketSize = Math.ceil(points.length / MAX_CHART_POINTS);
  const sampled: Array<ProfilePoint & { seconds: number }> = [points[0]];
  for (let i = 1; i < points.length - 1; i += bucketSize) {
    const bucket = points.slice(i, Math.min(i + bucketSize, points.length - 1));
    if (bucket.length === 0) continue;
    // Keep the deepest sample so the profile never loses its max depth,
    // but prefer a violation sample so ascent warnings survive downsampling
    const flagged = bucket.find(p => p.violation !== undefined);
    const deepest = bucket.reduce((a, b) => (b.depth > a.depth ? b : a));
    sampled.push(flagged ?? deepest);
  }
  sampled.push(points[points.length - 1]);
  return sampled;
};

const formatMinutesLabel = (minutes: number): string => {
  const m = Math.floor(minutes);
  const s = Math.round((minutes - m) * 60);
  return s > 0 ? `${m}m ${s}s` : `${m} min`;
};

const DiveProfileChart: React.FC<DiveProfileChartProps> = ({
  depthReadings,
  temperatureReadings = [],
  unitPreferences,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const useFeet = unitPreferences.depth === 'feet';
  const useFahrenheit = unitPreferences.temperature === 'fahrenheit';
  const depthUnit = useFeet ? 'ft' : 'm';
  const tempUnit = useFahrenheit ? '°F' : '°C';

  const { points, maxAscentRate, hasTemperature } = useMemo(() => {
    const { points: raw, maxAscentRate: rate } = buildProfile(depthReadings, temperatureReadings);
    const sampled = downsample(raw);
    const converted = sampled.map(p => ({
      ...p,
      depth: useFeet ? metersToFeet(p.depth) : p.depth,
      violation: p.violation !== undefined ? (useFeet ? metersToFeet(p.violation) : p.violation) : undefined,
      temperature:
        p.temperature !== undefined
          ? useFahrenheit
            ? celsiusToFahrenheit(p.temperature)
            : p.temperature
          : undefined,
    }));
    return {
      points: converted,
      maxAscentRate: rate,
      hasTemperature: converted.some(p => p.temperature !== undefined),
    };
  }, [depthReadings, temperatureReadings, useFeet, useFahrenheit]);

  if (points.length < 2) return null;

  const maxDepth = Math.max(...points.map(p => p.depth));
  const violationCount = points.filter(p => p.violation !== undefined).length;
  const displayAscentRate = useFeet ? metersToFeet(maxAscentRate) : maxAscentRate;
  const ascentLimit = useFeet ? metersToFeet(MAX_ASCENT_RATE_M_PER_MIN) : MAX_ASCENT_RATE_M_PER_MIN;
  const ascentExceeded = maxAscentRate > MAX_ASCENT_RATE_M_PER_MIN;
  const safetyStopTop = useFeet ? metersToFeet(SAFETY_STOP_TOP_M) : SAFETY_STOP_TOP_M;
  const safetyStopBottom = useFeet ? metersToFeet(SAFETY_STOP_BOTTOM_M) : SAFETY_STOP_BOTTOM_M;

  const depthColor = theme.palette.primary.main;
  const tempColor = theme.palette.warning.main;
  const violationColor = theme.palette.error.main;

  return (
    <Box mt={3}>
      <Paper elevation={2} sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} mb={2}>
          <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold' }}>
            <ShowChart color="primary" />
            Dive Profile
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              size="small"
              variant="outlined"
              color={ascentExceeded ? 'error' : 'success'}
              icon={ascentExceeded ? <Warning /> : undefined}
              label={`Max ascent ${displayAscentRate.toFixed(1)} ${depthUnit}/min`}
            />
            {ascentExceeded && (
              <Chip
                size="small"
                variant="outlined"
                color="error"
                label={`Exceeded ${ascentLimit.toFixed(0)} ${depthUnit}/min limit`}
              />
            )}
          </Box>
        </Box>

        <Box sx={{ width: '100%', height: 320 }}>
          <ResponsiveContainer>
            <ComposedChart data={points} margin={{ top: 8, right: hasTemperature ? 8 : 24, bottom: 8, left: 0 }}>
              <defs>
                <linearGradient id="depthFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={depthColor} stopOpacity={isDark ? 0.45 : 0.35} />
                  <stop offset="100%" stopColor={depthColor} stopOpacity={isDark ? 0.1 : 0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis
                dataKey="minutes"
                type="number"
                domain={[0, 'dataMax']}
                tickFormatter={(v: number) => `${Math.round(v)}`}
                label={{ value: 'Time (min)', position: 'insideBottom', offset: -2, fill: theme.palette.text.secondary, fontSize: 12 }}
                tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                stroke={theme.palette.divider}
              />
              <YAxis
                yAxisId="depth"
                reversed
                domain={[0, Math.ceil(maxDepth * 1.08)]}
                tickFormatter={(v: number) => `${Math.round(v)}`}
                label={{ value: `Depth (${depthUnit})`, angle: -90, position: 'insideLeft', fill: theme.palette.text.secondary, fontSize: 12 }}
                tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                stroke={theme.palette.divider}
              />
              {hasTemperature && (
                <YAxis
                  yAxisId="temp"
                  orientation="right"
                  domain={['auto', 'auto']}
                  tickFormatter={(v: number) => `${Math.round(v)}`}
                  label={{ value: `Temp (${tempUnit})`, angle: 90, position: 'insideRight', fill: theme.palette.text.secondary, fontSize: 12 }}
                  tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                  stroke={theme.palette.divider}
                />
              )}
              <Tooltip
                contentStyle={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 8,
                  color: theme.palette.text.primary,
                }}
                labelFormatter={(v: number) => formatMinutesLabel(v)}
                formatter={(value: number, name: string) => {
                  if (name === 'Depth') return [`${value.toFixed(1)} ${depthUnit}`, name];
                  if (name === 'Temperature') return [`${value.toFixed(1)} ${tempUnit}`, name];
                  if (name === 'Fast ascent') return [`${value.toFixed(1)} ${depthUnit}`, name];
                  return [value, name];
                }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <ReferenceArea
                yAxisId="depth"
                y1={safetyStopTop}
                y2={safetyStopBottom}
                fill={theme.palette.success.main}
                fillOpacity={isDark ? 0.12 : 0.08}
                label={{
                  value: 'Safety stop zone',
                  position: 'insideTopRight',
                  fill: theme.palette.success.main,
                  fontSize: 11,
                }}
              />
              <Area
                yAxisId="depth"
                type="monotone"
                dataKey="depth"
                name="Depth"
                stroke={depthColor}
                strokeWidth={2}
                fill="url(#depthFill)"
                dot={false}
                activeDot={{ r: 4 }}
                isAnimationActive={false}
              />
              {hasTemperature && (
                <Line
                  yAxisId="temp"
                  type="monotone"
                  dataKey="temperature"
                  name="Temperature"
                  stroke={tempColor}
                  strokeWidth={1.5}
                  strokeDasharray="5 3"
                  dot={false}
                  connectNulls
                  isAnimationActive={false}
                />
              )}
              {violationCount > 0 && (
                <Scatter
                  yAxisId="depth"
                  dataKey="violation"
                  name="Fast ascent"
                  fill={violationColor}
                  isAnimationActive={false}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </Box>

        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
          {ascentExceeded
            ? `Red points mark segments faster than the recommended ${ascentLimit.toFixed(0)} ${depthUnit}/min ascent rate. Slow, controlled ascents reduce decompression stress.`
            : `Ascent rate stayed within the recommended ${ascentLimit.toFixed(0)} ${depthUnit}/min limit throughout the dive. Nice control!`}
        </Typography>
      </Paper>
    </Box>
  );
};

export default DiveProfileChart;
